import {
  Base, DataTypes, SubmitTypes, ErrorTypes
} from '@mixins/forms'
import { mapGetters } from 'vuex'

const { ObjectDataType: dataType } = DataTypes
const { AjaxSubmit: submitType } = SubmitTypes
const { PreArrivalFormError: errorHandling } = ErrorTypes

const options = {
  name: 'pre-arrival-form',
  dataType,
  submitType,
  errorHandling,
  submitOnChange: false,
  skipValidation: false,
  tracking: {
    view: true,
    validation: true,
    submit: {},
    error: true
  },
}

export default {
  name: 'pre-arrival-form',
  mixins: [
    Base(options),
  ],
  props: {
    reservationId: {
      type: Number,
    },
    hotelId: {
      type: String,
    },
    existingPreferences: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      formValid: false,
      formData: {},
      hasErrors: false,
    }
  },
  computed: {
    ...mapGetters('auth', ['isAuthenticated']),
  },
  async mounted() {
    await this.createFormData()
    this.populateExistingPreferences()
    this.validateFormData()
    this.addEnterKeyListener()
    this.addUrlChangeListener()
  },
  methods: {
    addEnterKeyListener() {
      const inputElements = this.$el.querySelectorAll('input')
      inputElements.forEach((element) => {
        element.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            event.stopPropagation()
          }
        })
      })
    },
    async handleInput(event, inputType, isSingleSelect) {
      const element = event.target
      switch (inputType) {
        case 'checkbox':
          this.handleCheckboxInput(element, isSingleSelect)
          break
        case 'time':
          this.handleTimeInput(element, event)
          break
        default:
          this.handleStringInput(element)
          this.handleMaxLength(element)
          break
      }
      this.validateFormData()
    },
    handleTimeInput(element, event) {
      const inputWrapper = element.closest('.date-input-wrapper')
      const ampmToggle = inputWrapper.querySelector('.am-pm-toggle')
      const radioGroup = ampmToggle.querySelectorAll('input')
      const timePattern = /^(0[1-9]|1[0-2])[0-5][0-9]$|^$/
      const errorElement = element.closest('.date-input-wrapper').nextElementSibling
      const { errorMessage } = element.dataset
      let hourValue
      let ampmValue
      let finalValue
      switch (event.type) {
        case 'input':
          hourValue = element.value
          hourValue = hourValue.replace(/[^\d]/g, '')

          if (!timePattern.test(hourValue)) {
            this.setErrorMessage(errorElement, errorMessage)
          } else {
            ampmValue = ampmToggle.querySelector('input:checked')?.value
            hourValue = hourValue.replace(/(\d{2})(\d{2})/, '$1:$2')
            finalValue = !!ampmValue && !!hourValue ? `${hourValue} ${ampmValue}` : ''
            this.setErrorMessage(errorElement, '')
            this.handleStringInput(element, true, finalValue)
          }
          break
        case 'change':
          radioGroup.forEach((radio) => {
            radio.checked = radio === element
          })
          ampmValue = element.value
          hourValue = inputWrapper.querySelector('.time-text-input').value
          hourValue = hourValue.replace(/(\d{2})(\d{2})/, '$1:$2')
          finalValue = !!ampmValue && !!hourValue ? `${hourValue} ${ampmValue}` : ''
          this.handleStringInput(element, true, finalValue)
          break
        default:
          break
      }
    },
    handleCheckboxInput(element, isSingleSelect) {
      const itemCodeValue = element.dataset.codeValue
      const valueSelectedFromServer = this.checkServerData(itemCodeValue)
      const valueObj = this.createValueObj('checkbox', { element, valueSelectedFromServer })
      const { valueAlreadyInFormData } = this.checkFormData(valueObj)

      if (isSingleSelect === true) {
        this.handleSingleSelect(element, valueSelectedFromServer, valueObj, valueAlreadyInFormData)
      } else {
        this.handleMultiSelect(element, valueSelectedFromServer, valueObj, valueAlreadyInFormData)
      }
    },
    handleSingleSelect(element, valueSelectedFromServer, valueObj, valueAlreadyInFormData) {
      const inputElements = this.$el.querySelectorAll(`input[name="${element.name}"]`)
      const checkOnlyCurrentElement = () => {
        inputElements.forEach((input) => {
          input.checked = input === element
        })
      }
      const reselectPreselectedValue = (valObj) => {
        const preselectedValueInGroup = this.existingPreferences.find(
          (preference) => preference.value === valObj.preference.value
        )
        if (preselectedValueInGroup) {
          const preselectedElement = this.$el.querySelector(`input[value="${preselectedValueInGroup.value}"]`)
          const preselectedValueObj = this.createValueObj('checkbox', { element: preselectedElement, valueSelectedFromServer: { id: preselectedValueInGroup.id } })
          this.updateFormData('add-item', preselectedValueObj)
        }
      }
      const reselectGroupElement = (valObj) => {
        const groupHasElementSelectedFromServer = this.existingPreferences.find(
          (preference) => preference.value.startsWith(valObj.preference.value.substring(0, 3))
        )
        if (groupHasElementSelectedFromServer) {
          const groupElement = this.$el.querySelector(`input[data-code-value="${groupHasElementSelectedFromServer.value}"]`)
          const deleteObj = this.createValueObj('checkbox', { element: groupElement, valueSelectedFromServer: { id: groupHasElementSelectedFromServer.id } })
          this.updateFormData('add-item', deleteObj)
        }
      }

      if (valueSelectedFromServer) {
        if (element.checked) {
          this.updateFormData('wipe-group', valueObj)
          checkOnlyCurrentElement()
        } else {
          this.updateFormData('add-item', valueObj)
        }
      } else {
        this.updateFormData('wipe-group', valueObj)
        if (valueAlreadyInFormData) {
          element.checked = false
          this.updateFormData('remove-item', valueObj)
          reselectPreselectedValue(valueObj)
        } else {
          this.updateFormData('add-item', valueObj)
          checkOnlyCurrentElement()
        }
        reselectGroupElement(valueObj)
      }
      this.handleConditionalMessage(element, valueObj)
    },
    handleMultiSelect(element, valueSelectedFromServer, valueObj, valueAlreadyInFormData) {
      if (element.checked) {
        if (valueSelectedFromServer) {
          this.updateFormData('remove-item', valueObj)
        } else if (!valueAlreadyInFormData) {
          this.updateFormData('add-item', valueObj)
        }
      } else if (valueSelectedFromServer) {
        this.updateFormData('add-item', valueObj)
      } else {
        this.updateFormData('remove-item', valueObj)
      }
      this.handleConditionalMessage(element, valueObj)
    },
    handleStringInput(element, isDatePicker, computedValue) {
      const inputType = isDatePicker ? 'date' : 'string'
      const itemCode = isDatePicker ? element.closest('.date-input-wrapper').dataset.codeValue : element.dataset.codeValue
      const valueSelectedFromServer = this.checkServerData(itemCode)
      const textValueFromServer = valueSelectedFromServer?.note || ''
      const valueObj = this.createValueObj(
        inputType, { element, computedValue, valueSelectedFromServer }
      )
      const { valueAlreadyInFormData } = this.checkFormData(valueObj)
      if (textValueFromServer === element.value || textValueFromServer === computedValue) {
        this.updateFormData('remove-item', valueObj)
      } else if (valueAlreadyInFormData) {
        this.updateFormData('update-item', valueObj)
      } else {
        this.updateFormData('add-item', valueObj)
      }
    },
    createValueObj(inputType, configObj) {
      const { element, valueSelectedFromServer, computedValue } = configObj
      const preference = {
        code: 'OtherServicePreference',
        typeCode: 'Unknown',
        id: valueSelectedFromServer?.id || 0,
        hotelCode: this.hotelId,
        createdDateTime: valueSelectedFromServer?.createdDateTime || new Date().toISOString(),
        lastUpdateDateTime: new Date().toISOString()
      }
      let action
      if (inputType === 'checkbox') {
        action = valueSelectedFromServer ? 'Delete' : 'Create'
        preference.value = element.dataset.codeValue
      } else {
        const stringContent = inputType === 'date' ? computedValue : element.value.replace(/"/g, "'")
        const isEmpty = stringContent === ''
        preference.note = stringContent
        preference.value = inputType === 'date' ? element.closest('.date-input-wrapper').dataset.codeValue : element.dataset.codeValue
        if (valueSelectedFromServer?.id && isEmpty) {
          action = 'Delete'
        } else if (valueSelectedFromServer?.id && !isEmpty) {
          action = 'Update'
        } else {
          action = 'Create'
        }
      }
      return { action, preference }
    },
    checkFormData(valueObj) {
      const valueAlreadyInFormData = this.formData.preferences.value.some(
        (value) => value.preference.value === valueObj.preference.value
      )
      const itemInFormData = this.formData.preferences.value.find(
        (value) => value.preference.value === valueObj.preference.value
      )
      return { valueAlreadyInFormData, itemInFormData }
    },
    checkServerData(itemIdentifier) {
      return this.existingPreferences.find((preference) => preference.value === itemIdentifier)
    },
    updateFormData(operation, valueObj) {
      switch (operation) {
        case 'add-item':
          this.formData.preferences.value = [...this.formData.preferences.value, valueObj]
          break
        case 'update-item':
          this.formData.preferences.value = this.formData.preferences.value
            .map((value) => (
              value.preference.value === valueObj.preference.value ? valueObj : value
            ))
          break
        case 'remove-item':
          this.formData.preferences.value = this.formData.preferences.value
            .filter((value) => value.preference.value !== valueObj.preference.value)
          break
        case 'wipe-group': {
          const codePrefix = valueObj.preference.value.substring(0, 3)
          this.formData.preferences.value = this.formData.preferences.value.filter((item) => {
            const itemPrefix = item.preference.value.substring(0, 3)
            return itemPrefix !== codePrefix
          })
          break
        }
        default:
      }
    },
    setErrorMessage(errorElement, message) {
      if (message) {
        errorElement.textContent = message
        errorElement.classList.add('error')
        errorElement.classList.add('show')
        this.hasErrors = true
      } else {
        errorElement.textContent = ''
        errorElement.classList.remove('error')
        errorElement.classList.remove('show')
        this.hasErrors = false
      }
    },
    handleConditionalMessage(element, valueObj) {
      // NOTE: Hardcoded value. Change here or remove the next two lines if needed.
      const elementId = element.id
      const enableConditionalMessage = elementId === 'APT-TY'
      // END NOTE
      const { conditionalMessage } = element.dataset
      const messageArea = element.closest('fieldset').querySelector('.message-area')
      const { itemInFormData } = this.checkFormData(
        valueObj || this.createValueObj(element.type, { element })
      )
      const elementIsUnselected = itemInFormData?.action === 'Delete'
      const elementIsPreselectedFromServer = this.checkServerData(element.dataset.codeValue)
      const isElementSelected = (elementIsPreselectedFromServer || itemInFormData)
                              && !elementIsUnselected
      const displayMessage = enableConditionalMessage
                           && isElementSelected
                           && conditionalMessage
      if (displayMessage) {
        messageArea.textContent = conditionalMessage
        messageArea.classList.add('show')
      } else {
        messageArea.textContent = ''
        messageArea.classList.remove('show')
      }
    },
    handleMaxLength(element) {
      // NOTE: Hardcoded value. Change here or remove the method if needed.
      if (element.dataset.codeValue === 'APNOTE') {
        const maxLength = 150
        if (element.value.length > maxLength) {
          element.value = element.value.slice(0, maxLength)
        }
      }
      // END NOTE
    },
    handleCloseForm() {
      if (this.formData.preferences.value.length === 0) {
        this.$emit('close-form')
      } else {
        this.$emit('warning-modal')
      }
    },
    reloadPage() {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'instant',
      })
      setTimeout(() => {
        window.location.reload()
      }, 100)
    },
    redirectToPage(url) {
      window.location.href = url
    },
    addUrlChangeListener() {
      window.addEventListener('beforeunload', (e) => {
        if (this.formData.preferences.value.length !== 0) {
          e.preventDefault()
          e.returnValue = true
        }
      })
    },
    async createFormData() {
      this.formData.id = this.reservationId
      this.formData.preferences = {}
      this.formData.preferences.value = []
    },
    populateExistingPreferences() {
      this.existingPreferences.forEach((preference) => {
        const truncatedCodeValue = preference.value.substring(0, 100)
        const checkbox = this.$el.querySelector(`input[type="checkbox"][data-code-value="${truncatedCodeValue}"]`)
        const radio = this.$el.querySelector(`input[type="radio"][data-code-value="${truncatedCodeValue}"]`)
        const textInput = this.$el.querySelector(`input[type="text"][data-code-value="${preference.value}"]`)
        const textArea = this.$el.querySelector(`textarea[data-code-value="${preference.value}"]`)
        const dateWrapper = this.$el.querySelector(`.date-input-wrapper[data-code-value="${preference.value}"]`)

        if (checkbox) {
          checkbox.checked = true
          this.handleConditionalMessage(checkbox)
        }
        if (radio) radio.checked = true
        if (textInput) textInput.value = preference.note
        if (textArea) textArea.value = preference.note
        if (dateWrapper) {
          const timeFormat = /^\d{2}:\d{2} (AM|PM)$/
          if (!timeFormat.test(preference.note)) return

          let strippedValue = preference.note
          if (strippedValue.endsWith('AM') || strippedValue.endsWith('PM')) {
            strippedValue = strippedValue.slice(0, -2).trim()
          }
          const radioInput = dateWrapper.querySelector(`input[value="${preference.value.slice(-2)}"]`)
          if (radioInput) radioInput.checked = true
          const dateInput = dateWrapper.querySelector('input[type="text"]')
          dateInput.value = strippedValue
        }
      })
    },
    validateFormData() {
      const formValues = this.formData.preferences.value
      const atLeastOneInputHasChanged = Object.keys(formValues).length !== 0
      this.formValid = !this.hasErrors && atLeastOneInputHasChanged
    },
    async transformData() {
      return {
        ...this.formData,
      }
    },
    async transformResponse(response) {
      if (response?.success) {
        this.formData.preferences.value = []
        this.$emit('form-submitted')
      } else {
        this.$emit('submit-error')
      }
    },
  },
  render() {
    return this.$scopedSlots.default({
      formValid: this.formValid,
      handleInput: this.handleInput,
      handleCloseForm: this.handleCloseForm,
      serverErrorMsg: this.serverErrorMsg,
    })
  }
}
